<script setup>
/**
 * FormSaveBtn
 * Triggers the save action for a specific form via Pinia.
 * Manages button feedback (warning color if modified) and handles
 * post-save navigation logic based on the table name and current route.
 */
import {computed} from 'vue'
import {useRouter, useRoute} from 'vue-router'
import {useFormStore} from '@/stores/form'
import {useComponentFieldsStore} from '@/stores/componentFields'
import Button from '@/common/Button.vue'

const props = defineProps({
    formId: {
        type: String,
        required: true
    },
    buttonText: String,
    // cancelOnSave: used to suppress the component's internal onSave redirection logic
    cancelOnSave: Boolean,
    defaultBtnClass: String
})

const router = useRouter()
const route = useRoute()

const formStore = useFormStore(props.formId)
const componentFieldsStore = useComponentFieldsStore()

const currentData = computed(() => formStore.currentData || {})
const isModified = computed(() => formStore.isModified)
const isSaving = computed(() => formStore.isSaving)
const tableName = computed(() => formStore.tableName)
const hide = computed(() => tableName.value === 'files')

/**
 * Handles redirection and state updates after a successful save.
 */
const onSave = (item) => {
    if (tableName.value?.indexOf('product_variations__') === 0) {
        window.location = `/#/${tableName.value}?productId=${currentData.value.productId}`
    }
    // If we just saved a brand new item, redirect to its specific edit form or update context
    else if (formStore.id === 0 && item.id) {
        const routeName = route.name

        switch (true) {
            case routeName === 'componentFields':
                componentFieldsStore.setItems()
                router.push({params: {fieldId: item.id}})
                break

            case routeName === 'themesEditor' && route.query.section === 'content_types':
                componentFieldsStore.setItems()
                componentFieldsStore.activeEditId = item.id
                break

            default:
                router.push({
                    name: 'form',
                    params: {id: item.id},
                    query: route.query
                })
        }
    }
}

const onClick = () => {
    formStore.save().then((res) => {
        // Check for invalid flag returned by the store's validation logic
        if (!res?.invalid && !props.cancelOnSave) {
            onSave(res)
        }
    })
}
</script>

<template>
    <Button
        v-if="!hide"
        :disabled="isSaving"
        title="Save"
        :default-class="defaultBtnClass"
        :active-class="'btn-warning'"
        :active-value="isModified"
        @click="onClick"
    >
        <span v-if="buttonText" v-html="buttonText"/>
        <span v-else class="bi-save"/>
    </Button>
</template>